import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import DataTable from "react-data-table-component";
import Octicon from "react-octicon";

import { BSBtn, BSCard } from "traec-react/utils/bootstrap";

import InviteForm from "./form";
import { CompanyPermission } from "storybook-dashboard/permissions/company";
import { ProjectPermission } from "storybook-dashboard/permissions/project";
import { BSBtnDropdown } from "traec-react/utils/bootstrap";
import { confirmDelete } from "traec-react/utils/sweetalert";

import { formEventToData } from "AppSrc/utils";
import useApi, { doFetch } from "storybook-dashboard/utils/fetching";
import { useModal, Modal } from "storybook-dashboard/components/modal";
import { Hide, nestByDunderKeys } from "storybook-dashboard/utils";
import { getRoleName } from "../companyUser/utils";
import Im from "immutable";
import Moment from "moment";
import { format } from "d3";
import { RoleTypesGuide } from "../companyUser/tree";

const deleteMember = ({ projectId, companyId, member }) => {
  let memberName = `${member.getIn(["user", "first_name"])} ${member.getIn(["user", "last_name"])}`;
  let ID = projectId || companyId;
  confirmDelete({
    text: `This will delete ${
      projectId ? "project" : "company"
    } member ${memberName}.  Are you sure you would like to proceed?`,
    onConfirm: () => {
      doFetch(`/api/${projectId ? "project" : "company"}/member/${member.get("uid")}/`, "DELETE");
    },
  });
};

const pushMemberToBelow = (member) => {
  doFetch(`/api/company/member/${member.get("uid")}/`, "PATCH", { pushBelow: true });
};

const sendInvite = (e, { projectId, companyId, onSuccess }) => {
  let data = nestByDunderKeys(Im.fromJS(formEventToData(e)))?.toJS();
  const _type = projectId ? "project" : "company";
  const _id = projectId ? projectId : companyId;
  console.log("sendInvite", data);
  doFetch(`/api/${_type}/${_id}/invite/`, "POST", data)
    .then((response) => {
      console.log("Invite sent successfully", response);
      if (onSuccess) onSuccess(response);
    })
    .catch((error) => console.error("Error sending invite", error));
};

const exportToCSV = (data, entityName, entityType) => {
  if (!data || data.size === 0) return;

  // Define CSV headers
  const headers = ["Name", "Email", "Permissions", "Last Login"];

  // Convert data to CSV format
  const csvContent = [
    headers.join(","),
    ...data.map((user) => {
      const fullName = `${user.getIn(["user", "first_name"], "")} ${user.getIn(["user", "last_name"], "")}`.trim();
      const email = user.getIn(["user", "email"], "");
      const permissions = user.getIn(["auth", "name"], "No permissions assigned");
      const lastLogin = user.getIn(["user", "last_login"]);
      const formattedLastLogin = Moment(lastLogin).isValid() ? Moment(lastLogin).format("DD MMM YYYY") : "Never";

      // Escape CSV fields that contain commas or quotes
      const escapeCSVField = (field) => {
        if (typeof field === "string" && (field.includes(",") || field.includes('"') || field.includes("\n"))) {
          return `"${field.replace(/"/g, '""')}"`;
        }
        return field;
      };

      return [
        escapeCSVField(fullName),
        escapeCSVField(email),
        escapeCSVField(permissions),
        escapeCSVField(formattedLastLogin),
      ].join(",");
    }),
  ].join("\n");

  // Create and download the file
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `${entityName || entityType}_users_${Moment().format("YYYY-MM-DD")}.csv`);
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

// Get the last login date formatted for display in local time
export const getLastLoginFormatted = (lastLogin) => {
  return Moment.utc(lastLogin).isValid() ? Moment.utc(lastLogin).local().format("DD MMM YY HH:mm") : "Never";
};

const getColumns = (props) => {
  const { projectId, companyId, includeBelowLevel } = props;
  let Permission = projectId ? ProjectPermission : CompanyPermission;

  const columns = [
    {
      name: "Name",
      selector: (row) => `${row.getIn(["user", "first_name"])} ${row.getIn(["user", "last_name"])}`,
      sortable: true,
      grow: 2,
      wrap: true,
      cell: (row) => {
        const fullName = `${row.getIn(["user", "first_name"])} ${row.getIn(["user", "last_name"])}`;
        return (
          <div className="d-flex flex-column">
            <span className="fw-medium">{fullName}</span>
          </div>
        );
      },
    },
    {
      name: "Email",
      selector: (row) => row.getIn(["user", "email"]),
      sortable: true,
      grow: 2,
      wrap: true,
      cell: (row) => <span className="text-muted">{row.getIn(["user", "email"])}</span>,
    },
    {
      name: (
        <span className="align-middle text-center">
          {projectId ? "Role" : "Permissions"}
          {includeBelowLevel ? (
            ""
          ) : (
            <>
              <br />
              {" at this Level"}
            </>
          )}
        </span>
      ),
      selector: (row) => (projectId ? row.getIn(["project_discipline", "name"]) : row.getIn(["auth", "name"])),
      sortable: true,
      grow: 1,
      wrap: true,
      cell: (row) => {
        const roleName = projectId ? row.getIn(["project_discipline", "name"]) : row.getIn(["auth", "name"]);
        return roleName ? (
          <span className="badge bg-light text-dark">{roleName}</span>
        ) : (
          <span className="text-muted">No role assigned</span>
        );
      },
    },
    {
      name: "Last login",
      selector: (row) => row.getIn(["user", "last_login"]),
      format: (row) => getLastLoginFormatted(row.getIn(["user", "last_login"])),
      sortable: true,
      grow: 1,
      wrap: true,
      cell: (row) => {
        const lastLogin = row.getIn(["user", "last_login"]);
        const formatted = getLastLoginFormatted(lastLogin);
        const isRecent = Moment.utc(lastLogin).isAfter(Moment().subtract(30, "days"));
        return <small className={"text-muted"}>{formatted}</small>;
      },
    },
    {
      name: "Actions",
      cell: (row) => {
        const userId = row.getIn(["user", "pk"]);
        const userManagementUrl = `/company/${companyId}/user/${userId}`;

        return (
          <div className="d-flex gap-1">
            <Link
              to={userManagementUrl}
              className="btn btn-sm btn-outline-primary"
              title="Manage User Roles & Permissions"
            >
              <Octicon name="gear" />
            </Link>
          </div>
        );
      },
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "100px",
    },
  ];

  return columns;
};

function MemberListHeader({ companyId, projectId, memberCount, isLoading }) {
  const { data: company } = useApi(`/api/company/{_companyId}/`, { _companyId: companyId });
  const { data: project } = useApi(`/api/project/{projectId}/`, { projectId });

  const entityName = projectId ? project?.get("name") : company?.get("name");
  const entityType = projectId ? "Project" : "Company";

  return (
    <div className="card shadow-sm mb-4">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <div className="d-flex align-items-center mb-2">
              <h1 className="h3 mb-0 text-primary">Users</h1>
            </div>
            <h6 className="text-muted font-weight-bold mb-2">{entityName}</h6>
            {!isLoading && (
              <p className="text-muted small mb-0">
                <strong>Total users:</strong> {memberCount}
              </p>
            )}
          </div>
          <div className="text-end">
            <span className="badge badge-primary">{entityType} Users</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function PageDescription({ projectId, includeBelowLevel }) {
  return (
    <div className="mb-4">
      <h2 className="h4 text-dark mb-2">User Management</h2>
      <p className="text-muted">
        {projectId
          ? "Manage project users and their roles. You can invite new users, view their roles, and manage their permissions."
          : "Manage organisation users and their permissions. You can invite new users, assign permissions, and control their access levels across different areas of your organisation."}
      </p>
      {!projectId && (
        <div className="alert alert-info">
          <div className="d-flex">
            <Octicon name="info" className="me-2 mt-1" />
            <div>
              <strong>Multi-level view:</strong> Use the checkbox below to include users from sub-organisations. This
              gives you a complete overview of all users within your organisational hierarchy.
            </div>
          </div>
        </div>
      )}
      <div className="card-body border rounded py-3">
        <RoleTypesGuide />
      </div>
    </div>
  );
}

function InviteNewUserModal({ projectId, companyId, setModal }) {
  const { hideModal } = useModal();

  let url = projectId ? `/api/project/{projectId}/member/` : `/api/company/{companyId}/member/`;
  const { trigger: triggerThisLevel } = useApi(url, { projectId, companyId });
  const { trigger: triggerBelowLevel } = useApi(url + "?includeBelowLevel=true", { projectId, companyId });

  const onSuccess = () => {
    triggerThisLevel();
    triggerBelowLevel();
    hideModal();
  };

  return (
    <Modal title="Invite New User" setModal={setModal}>
      <div className="p-3">
        <p className="text-muted mb-3">
          {projectId
            ? "Invite a new user to join this project. They will receive an email invitation with instructions to access the project."
            : "Invite a new user to join your organisation. They will receive an email invitation with instructions to create their account and access your organisation."}
        </p>
        <InviteForm
          {...{ companyId, projectId }}
          onSubmit={(e) => sendInvite(e, { projectId, companyId, onSuccess })}
        />
      </div>
    </Modal>
  );
}

const userRoleSummary = (members) => {
  return members
    .reduce((acc, m) => {
      const userId = m.getIn(["user", "pk"]);
      return acc.update(userId, (existing) => {
        if (existing) {
          return existing.update("roles", (roles) => roles.add(getRoleName(m)));
        } else {
          return Im.Map()
            .set("user", m.get("user"))
            .set("roles", Im.Set([getRoleName(m)]));
        }
      });
    }, Im.Map())
    .toList()
    .map((i) => i.setIn(["auth", "name"], i.get("roles").toList().join(", ")));
};

export default function MemberList(props) {
  const [includeBelowLevel, setIncludeBelowLevel] = useState(false);
  const [allLevelsData, setAllLevelsData] = useState(Im.List());
  const [searchTerm, setSearchTerm] = useState("");
  const { setModal } = useModal();
  let { companyId, projectId } = props;

  // Get entity data for export filename
  const { data: company } = useApi(`/api/company/{_companyId}/`, { _companyId: companyId });
  const { data: project } = useApi(`/api/project/{projectId}/`, { projectId });

  let baseUrl = projectId ? `/api/project/{projectId}` : `/api/company/{companyId}`;
  let url = baseUrl + "/member/" + (includeBelowLevel ? "?includeBelowLevel=true" : "");
  const { data: members, isLoading } = useApi(url, { projectId, companyId });

  // Pre-load some data for the form
  useApi(baseUrl + "/authgroup/", { projectId, companyId });
  useApi("/api/project/{projectId}/discipline/", { projectId });

  // Nest the dunder-separated data returned from the endpoint
  useEffect(() => {
    if (includeBelowLevel && members) {
      const nestedMembers = members.map((i) => nestByDunderKeys(i));
      setAllLevelsData(userRoleSummary(nestedMembers));
    }
  }, [members, includeBelowLevel]);

  let PermissionRender = projectId ? ProjectPermission : CompanyPermission;
  let data = includeBelowLevel ? allLevelsData : members;

  // Filter data based on search term
  const filteredData =
    data?.filter((user) => {
      if (!searchTerm) return true;

      const firstName = user.getIn(["user", "first_name"], "").toLowerCase();
      const lastName = user.getIn(["user", "last_name"], "").toLowerCase();
      const email = user.getIn(["user", "email"], "").toLowerCase();
      const fullName = `${firstName} ${lastName}`.toLowerCase();
      const search = searchTerm.toLowerCase();

      return fullName.includes(search) || email.includes(search);
    }) || Im.List();

  const userCount = data?.size || 0;
  const filteredUserCount = filteredData?.size || 0;

  console.log("UserList", companyId, url, data?.toJS());

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12">
          <MemberListHeader companyId={companyId} projectId={projectId} memberCount={userCount} isLoading={isLoading} />

          <div className="card shadow-sm">
            <div className="card-body">
              <PageDescription projectId={projectId} includeBelowLevel={includeBelowLevel} />

              <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="d-flex align-items-center gap-3">
                  <Hide when={!companyId}>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="includeBelowLevel"
                        checked={includeBelowLevel}
                        onChange={(e) => setIncludeBelowLevel(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="includeBelowLevel">
                        Include users from sub-organisations
                      </label>
                    </div>
                  </Hide>
                </div>

                <PermissionRender {...props} requiresAdmin={true}>
                  <button
                    className="btn btn-sm btn-primary d-flex align-items-center gap-2"
                    onClick={(e) => setModal(<InviteNewUserModal {...props} setModal={setModal} />)}
                  >
                    <Octicon name="plus" />
                    Invite New User
                  </button>
                </PermissionRender>
              </div>

              {/* Search Bar */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0">
                      <Octicon name="search" className="text-muted" />
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0"
                      placeholder="Search users by name or email..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{ borderLeft: "none" }}
                    />
                    {searchTerm && (
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        type="button"
                        onClick={() => setSearchTerm("")}
                        title="Clear search"
                      >
                        <Octicon name="x" />
                      </button>
                    )}
                  </div>
                  {searchTerm && (
                    <small className="text-muted mt-1 d-block">
                      Showing {filteredUserCount} of {userCount} users
                    </small>
                  )}
                </div>
                <div className="col-md-6 d-flex justify-content-end align-items-start">
                  <button
                    className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-2"
                    onClick={() => {
                      const entityName = projectId ? project?.get("name") : company?.get("name");
                      const entityType = projectId ? "Project" : "Company";
                      exportToCSV(filteredData, entityName, entityType);
                    }}
                    disabled={filteredUserCount === 0}
                    title="Export user list to CSV"
                  >
                    <Octicon className="mr-1" name="cloud-download" />
                    Export CSV
                  </button>
                </div>
              </div>

              <Hide when={!isLoading}>
                <div className="d-flex justify-content-center align-items-center py-5">
                  <div className="spinner-border text-primary me-3 mr-3" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                  <span className="text-muted">Loading users...</span>
                </div>
              </Hide>

              <Hide when={isLoading}>
                <div className="table-responsive">
                  <DataTable
                    columns={getColumns({ ...props, includeBelowLevel })}
                    data={filteredData?.toArray() || []}
                    highlightOnHover={true}
                    pagination
                    paginationPerPage={25}
                    paginationRowsPerPageOptions={[25, 50, 100]}
                    noDataComponent={
                      <div className="text-center py-5">
                        <Octicon
                          name={searchTerm ? "search" : "person"}
                          className="text-muted mb-3"
                          style={{ fontSize: "48px" }}
                        />
                        <h5 className="text-muted">{searchTerm ? "No matching users found" : "No users found"}</h5>
                        <p className="text-muted">
                          {searchTerm
                            ? `No users match your search for "${searchTerm}". Try adjusting your search terms.`
                            : projectId
                            ? "This project doesn't have any users yet. Start by inviting Users."
                            : "This organisation doesn't have any users yet. Start by inviting users."}
                        </p>
                        {searchTerm ? (
                          <button className="btn btn-outline-primary" onClick={() => setSearchTerm("")}>
                            <Octicon name="x" className="me-2" />
                            Clear Search
                          </button>
                        ) : (
                          <PermissionRender {...props} requiresAdmin={true}>
                            <button
                              className="btn btn-outline-primary"
                              onClick={(e) => setModal(<InviteNewUserModal {...props} setModal={setModal} />)}
                            >
                              <Octicon name="plus" className="me-2" />
                              Invite First User
                            </button>
                          </PermissionRender>
                        )}
                      </div>
                    }
                    customStyles={{
                      table: {
                        style: {
                          borderRadius: "8px",
                        },
                      },
                      headRow: {
                        style: {
                          backgroundColor: "#f8f9fa",
                          borderTopLeftRadius: "8px",
                          borderTopRightRadius: "8px",
                        },
                      },
                      rows: {
                        style: {
                          "&:hover": {
                            backgroundColor: "#f8f9fa",
                            cursor: "pointer",
                          },
                        },
                      },
                    }}
                  />
                </div>
              </Hide>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
